/**
 * useRoll — Roll dispatch hook with debounce and timeout.
 *
 * Uses typed roll builders from shared/systems/dnd5e/rolls.ts.
 * Separate from existing useRolls.ts (BI-006).
 *
 * T002: Parameter type is `RulesCharacterData` — the unified structural
 * supertype from shared/systems/dnd5e/rules/index.ts. `HudCharacterData`
 * (src/lib/character-hud-store.ts) is directly assignable to it without
 * any `as unknown as` casts, so callers can pass their HUD data through
 * unchanged. See structural contract docs on both type definitions.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  rollAbilityCheck,
  rollSave,
  rollSkill,
  rollAttack,
  rollDamage,
  rollSpellAttack,
  rollInitiative,
} from '@roll1/shared/systems/dnd5e/rolls.js';
import type { AbilityStat, SkillId, RulesCharacterData } from '@roll1/shared/systems/dnd5e/rules/index.js';
import { toast } from 'sonner';
import { useI18n } from '../i18n';
import { takeRollMode } from '../lib/roll-mode-store';

/** Attack data shape from HudCharacterData.combat.attacks */
interface RollAttackData {
  id: string;
  name: string;
  attackBonus: number;
  damage: string;
  damageType: string;
  meta?: string;
}

export interface UseRollReturn {
  rollSkill: (skill: SkillId) => void;
  rollSave: (stat: AbilityStat) => void;
  rollAbilityCheck: (stat: AbilityStat) => void;
  rollAttack: (attack: RollAttackData) => void;
  rollDamage: (attack: RollAttackData) => void;
  rollSpellAttack: (spell?: { name: string; damage?: { expression: string; type: string } }) => void;
  rollInitiative: () => void;
  rollCustom: (name: string, expression: string) => void;
  isPending: boolean;
  /** Clear pending state (call when roll:result is received externally) */
  clearPending: () => void;
}

const DEBOUNCE_MS = 300;
const TIMEOUT_MS = 5000;

export type SendFn = (msg: any) => void;
/** Subscribe to incoming WS messages; returns unsubscribe function */
export type OnMessageFn = (handler: (msg: any) => void) => () => void;

/**
 * @param data Character data — accepts any object structurally assignable
 *   to `RulesCharacterData`, including the frontend `HudCharacterData`
 *   from src/lib/character-hud-store.ts (no cast required).
 */
export function useRoll<T extends RulesCharacterData>(
  sheetId: string,
  data: T,
  send: SendFn,
  onMessage?: OnMessageFn,
): UseRollReturn {
  const { t } = useI18n();
  const [isPending, setIsPending] = useState(false);
  const lastCallRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSheetIdRef = useRef<string | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // V004: Subscribe to roll:result to cancel timeout when result arrives
  useEffect(() => {
    if (!onMessage) return;
    return onMessage((msg: any) => {
      if (msg.type === 'roll:result' && timeoutRef.current) {
        // Match by sheetId if available, or just clear any pending roll
        if (!pendingSheetIdRef.current || msg.rollResult?.sheetId === pendingSheetIdRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
          pendingSheetIdRef.current = null;
          setIsPending(false);
          // On a phone the chat is another tab: the result of a roll made on the sheet shows on the sheet too
          // (charsheet pass №55 — «бросок ушёл в чат, на листе ничего»).
          const rr = msg.rollResult as { rollName?: string; results?: Array<{ total: number; isCritical?: boolean }>; damage?: { total: number; type?: string }; kept?: number; mode?: string } | undefined;
          const narrow = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches;
          if (narrow && rr?.results?.length) {
            const totals = rr.kept !== undefined && rr.results[rr.kept]
              ? `${rr.results[rr.kept].total} (${rr.mode === 'advantage' ? t('rollCard.advantage') : t('rollCard.disadvantage')})`
              : rr.results.map(r => r.total).join(' / ');
            const dmg = rr.damage ? ` · ${t('characterHud.rollDamage')} ${rr.damage.total}${rr.damage.type ? ` ${rr.damage.type}` : ''}` : '';
            toast(`${rr.rollName ?? ''}: ${totals}${dmg}`, { id: 'sheet-roll-result' });
          }
        }
      }
    });
  }, [onMessage, t]);

  const dispatch = useCallback((rollRequest: { sheetId: string; rollName: string; expression: string; fieldValues: Record<string, number>; isWhisper: boolean; isDualD20: boolean; attackDamage?: { expression: string; type: string }; rollKind?: string; stat?: string }) => {
    const now = Date.now();
    if (now - lastCallRef.current < DEBOUNCE_MS) return;
    lastCallRef.current = now;

    pendingSheetIdRef.current = rollRequest.sheetId;
    // GN (Н4): the player's advantage / disadvantage for this one d20 roll (the toggle on the sheet / HUD).
    const manual = rollRequest.rollKind ? takeRollMode(rollRequest.sheetId) : 'normal';
    send({ type: 'roll:request', ...rollRequest, ...(manual !== 'normal' ? { advantage: manual } : {}) });
    setIsPending(true);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      pendingSheetIdRef.current = null;
      setIsPending(false);
      toast(t('characterHud.rollTimeout'));
    }, TIMEOUT_MS);
  }, [send, t]);

  const doRollSkill = useCallback((skill: SkillId) => {
    dispatch(rollSkill(sheetId, skill, data));
  }, [sheetId, data, dispatch]);

  const doRollSave = useCallback((stat: AbilityStat) => {
    dispatch(rollSave(sheetId, stat, data));
  }, [sheetId, data, dispatch]);

  const doRollAbilityCheck = useCallback((stat: AbilityStat) => {
    dispatch(rollAbilityCheck(sheetId, stat, data));
  }, [sheetId, data, dispatch]);

  const doRollAttack = useCallback((attack: RollAttackData) => {
    dispatch(rollAttack(sheetId, attack, data));
  }, [sheetId, data, dispatch]);

  const doRollDamage = useCallback((attack: RollAttackData) => {
    dispatch(rollDamage(sheetId, attack));
  }, [sheetId, dispatch]);

  const doRollSpellAttack = useCallback((spell?: { name: string; damage?: { expression: string; type: string } }) => {
    dispatch(rollSpellAttack(sheetId, data, spell));
  }, [sheetId, data, dispatch]);

  const doRollInitiative = useCallback(() => {
    dispatch(rollInitiative(sheetId, data));
  }, [sheetId, data, dispatch]);

  const doRollCustom = useCallback((name: string, expression: string) => {
    dispatch({
      sheetId,
      rollName: name,
      expression,
      fieldValues: {},
      isWhisper: false,
      isDualD20: false,
    });
  }, [sheetId, dispatch]);

  // V004: expose clearPending so callers can cancel timeout when roll:result arrives
  const clearPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPending(false);
  }, []);

  return {
    rollSkill: doRollSkill,
    rollSave: doRollSave,
    rollAbilityCheck: doRollAbilityCheck,
    rollAttack: doRollAttack,
    rollDamage: doRollDamage,
    rollSpellAttack: doRollSpellAttack,
    rollInitiative: doRollInitiative,
    rollCustom: doRollCustom,
    isPending,
    clearPending,
  };
}
